import { defineStore } from 'pinia'
import {
  featureFlagProvider,
  defaultForFlag,
  type FeatureFlagContext,
  type FeatureFlagState,
} from '@/config/featureFlags'

interface FeatureFlagsState {
  flags: Record<string, FeatureFlagState>
  ready: boolean
}

export const useFeatureFlagsStore = defineStore('featureFlags', {
  state: (): FeatureFlagsState => ({
    flags: {},
    ready: false,
  }),
  getters: {
    isEnabled: (state) => {
      return (key: string, fallback?: boolean): boolean => {
        const flag = state.flags[key]
        if (flag === undefined) return fallback ?? defaultForFlag(key)
        return flag.enabled
      }
    },
    value: (state) => {
      return function <T = string>(key: string, fallback?: T): T {
        const flag = state.flags[key]
        if (
          flag === undefined ||
          flag.value === undefined ||
          flag.value === null
        )
          return fallback as T
        return flag.value as unknown as T
      }
    },
  },
  actions: {
    /** Inicializa el proveedor e hidrata el snapshot de flags. */
    async load(context?: FeatureFlagContext) {
      try {
        // Re-hidrata ante cualquier cambio del proveedor (refetch tras cache,
        // cambio de identidad, flip remoto en el panel) sin recargar la página.
        featureFlagProvider.subscribe(() => {
          this.flags = featureFlagProvider.getAllFlags()
        })
        await featureFlagProvider.init(context)
        this.flags = featureFlagProvider.getAllFlags()
      } catch (error) {
        console.error('[featureFlags] load falló; se usan defaults.', error)
      } finally {
        this.ready = true
      }
    },
    /** Re-evalúa flags para un nuevo contexto (login, cambio de compañía). */
    async identify(context: FeatureFlagContext) {
      try {
        await featureFlagProvider.identify(context)
        this.flags = featureFlagProvider.getAllFlags()
      } catch (error) {
        console.error('[featureFlags] identify falló.', error)
      }
    },
    /** Limpia la identidad y vuelve al contexto anónimo (logout). */
    async clear() {
      try {
        await featureFlagProvider.reset()
        this.flags = featureFlagProvider.getAllFlags()
      } catch (error) {
        console.error('[featureFlags] clear falló.', error)
      }
    },
  },
})
